import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, filter, from, map, merge, tap } from 'rxjs';

import { JobInvoiceActions } from '../job/job-invoice/job-invoice.actions';

import { OverviewTimelineActions } from '../job/job-overview/overview-timeline/overview-timeline.actions';
import { EventItineraryActions } from '../job/job-overview/overview-timeline/timeline-book-event/event-itinerary/event-itinerary.actions';
import { BookEventDialogActions } from '../job/job-overview/overview-timeline/timeline-book-event/timeline-book-event.actions';
import { CreateEventDialogActions } from '../job/job-overview/overview-timeline/timeline-create-event/timeline-create-event.actions';
import { JobToolActions } from '../job/job-tool.actions';
import { DetailsHelperService } from '../services/details-helper.service';
import { CancelAndCloseDialogActions } from '../shared/cancel-and-close-dialog/cancel-and-close-dialog.actions';

export const propagateEventUpdates = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {

    const singleEventEmitted$ = actions$.pipe(
      ofType(
        CreateEventDialogActions.updateEventSuccess,
        OverviewTimelineActions.updateEventStatusSuccess,
        BookEventDialogActions.bookEventSuccess,
        BookEventDialogActions.rescheduleEventSuccess,
        EventItineraryActions.saveEventChangesSuccess,
        JobToolActions.updateEventLocationsSuccess,
      ),
      map((payload) => payload.updatedEvent.id),
    );

    const singleEventIdEmitted$ = actions$.pipe(
      ofType(
        OverviewTimelineActions.deleteEventSuccess,
      ),
      map((payload) => payload.eventId),
    );

    const multipleEventsEmitted$ = actions$.pipe(
      ofType(
        CancelAndCloseDialogActions.cancelEventsSuccess,
      ),
      concatMap(({ updatedEvents }) => from(updatedEvents)),
      map((event) => event.id),
    );

    return merge(singleEventEmitted$, singleEventIdEmitted$, multipleEventsEmitted$).pipe(
      filter(Boolean),
      tap((eventId: string) => detailsHelper.pushUpdate({
        action: 'update',
        type: 'Events',
        id: eventId,
      })),
    );
}, { functional: true, dispatch: false });

export const propagateJobUpdates = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(
        JobToolActions.saveChangesSuccess,
        CancelAndCloseDialogActions.cancelAndCloseSuccess,
      ),
      tap(({ updatedJob }) => detailsHelper.pushUpdate({
        action: 'update',
        type: 'Jobs',
        id: updatedJob.id,
      })),
    );
}, { functional: true, dispatch: false });

export const propagateInvoiceUpdates = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.createInvoiceSuccess),
      tap(({ invoice }) => detailsHelper.pushUpdate({
        id: invoice.id,
        type: 'Invoice',
        action: 'create',
      })),
    );
}, { functional: true, dispatch: false });

export const propagateFinalizedInvoice = createEffect((
  actions$ = inject(Actions),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(JobInvoiceActions.finalizeInvoiceSuccess),
      tap(({ invoiceId }) => detailsHelper.pushUpdate({
        id: invoiceId,
        type: 'Invoice',
        action: 'update',
      })),
    );
}, { functional: true, dispatch: false });
